// The three device categories and the plain-language "why". Pure —
// unit-tested in __tests__/category.test.js.
import { REQUIREMENT_STATUS } from './requirements';
import { DEVICE_CATEGORY_RULES } from './thresholds';

const SHARED_REQUIREMENTS = rules => [
  `RAM ${rules.minRamGb} GB or more`,
  `Windows ${rules.minWindowsVersion}+, macOS ${rules.minMacOsVersion}+, Android ${rules.minAndroidVersion}+, iOS ${rules.minIosVersion}+ (iPhone ${rules.minIphoneModel} or newer) or iPadOS ${rules.minIpadOsVersion}+`,
];

export const DEVICE_CATEGORY = Object.freeze({
  SMOOTH: 'smooth',
  LIMITED: 'limited',
  NOT_SUPPORTED: 'not-supported',
});

/** Display copy for each category, in order best → worst. */
export const describeCategories = (rules = DEVICE_CATEGORY_RULES) => [
  {
    category: DEVICE_CATEGORY.SMOOTH,
    title: 'Smooth',
    color: '#0B834F',
    summary: 'Exercises run smoothly.',
    requirements: [`FPS above ${rules.smoothAboveFps}`, ...SHARED_REQUIREMENTS(rules)],
  },
  {
    category: DEVICE_CATEGORY.LIMITED,
    title: 'Limited',
    color: '#FC9D14',
    summary: 'Exercises work, but tracking is less smooth.',
    requirements: [
      `FPS above ${rules.limitedAboveFps} (up to ${rules.smoothAboveFps})`,
      ...SHARED_REQUIREMENTS(rules),
    ],
  },
  {
    category: DEVICE_CATEGORY.NOT_SUPPORTED,
    title: 'Not supported',
    color: '#D92D20',
    summary: 'The app does not work on this device.',
    requirements: [
      `FPS ${rules.limitedAboveFps} or below`,
      `or RAM below ${rules.minRamGb} GB`,
      `or Windows below ${rules.minWindowsVersion} / macOS below ${rules.minMacOsVersion} / Android below ${rules.minAndroidVersion} / iOS below ${rules.minIosVersion} / iPadOS below ${rules.minIpadOsVersion}`,
      'or the camera / pose model cannot start',
    ],
  },
];

export const categoryInfo = (category, rules = DEVICE_CATEGORY_RULES) =>
  describeCategories(rules).find(item => item.category === category) ?? null;

/** Category for one FPS reading alone — what the live badges use. */
export function categoryForFps(fps, rules = DEVICE_CATEGORY_RULES) {
  if (typeof fps !== 'number' || !Number.isFinite(fps)) {
    return null;
  }
  if (fps > rules.smoothAboveFps) {
    return DEVICE_CATEGORY.SMOOTH;
  }
  return fps > rules.limitedAboveFps ? DEVICE_CATEGORY.LIMITED : DEVICE_CATEGORY.NOT_SUPPORTED;
}

/**
 * @param {{ averageFps: number|null, requirements: object, fatalError?: string|null }} input
 * @returns {{ category: string, reasons: string[] }}
 */
export function classifyDevice(
  { averageFps, requirements, fatalError = null },
  rules = DEVICE_CATEGORY_RULES
) {
  const reasons = [];

  if (fatalError) {
    reasons.push(`Could not start: ${fatalError}`);
  }
  const { ram, os } = requirements ?? {};
  if (ram?.status === REQUIREMENT_STATUS.FAIL) {
    reasons.push(`RAM is ${ram.valueGb} GB — at least ${ram.requiredGb} GB is required.`);
  }
  if (os?.status === REQUIREMENT_STATUS.FAIL) {
    reasons.push(`${os.name} ${os.version} — ${os.required} is required.`);
  }

  const fpsCategory = categoryForFps(averageFps, rules);
  if (!fatalError && fpsCategory === null) {
    reasons.push('No pose result was measured.');
  } else if (fpsCategory === DEVICE_CATEGORY.SMOOTH) {
    reasons.push(`Average FPS ${averageFps} is above ${rules.smoothAboveFps}.`);
  } else if (fpsCategory === DEVICE_CATEGORY.LIMITED) {
    reasons.push(
      `Average FPS ${averageFps} is above ${rules.limitedAboveFps} but not above ${rules.smoothAboveFps}.`
    );
  } else if (fpsCategory === DEVICE_CATEGORY.NOT_SUPPORTED) {
    reasons.push(
      `Average FPS ${averageFps} is ${rules.limitedAboveFps} or below — too low for tracking to work.`
    );
  }

  const blocked = Boolean(fatalError) || fpsCategory === null || requirements?.passes === false;
  return { category: blocked ? DEVICE_CATEGORY.NOT_SUPPORTED : fpsCategory, reasons };
}

/**
 * Short lines on why the FPS is what it is, from the run closest to the average. `cause` lines
 * are the likely bottlenecks; `info` lines are context.
 * @returns {{ kind: 'cause'|'info', text: string }[]}
 */
export function explainFps(measured, environment, rules = DEVICE_CATEGORY_RULES) {
  if (!measured || measured.error) {
    return [];
  }
  const lines = [];
  const cause = text => lines.push({ kind: 'cause', text });
  const info = text => lines.push({ kind: 'info', text });
  const {
    deliveryFps,
    processingFps,
    cameraFps,
    avgInferenceMs,
    maxInferenceFps,
    busySkipPercent,
    maxThrottleLevel,
  } = measured;

  // The category counts reused results too; say how much of it is real tracking.
  if (deliveryFps !== null && processingFps !== null && processingFps < deliveryFps * 0.8) {
    cause(
      `Only ${processingFps} of these ${deliveryFps} FPS are fresh pose results — the rest repeat the last result because the model cannot keep up.`
    );
  } else if (processingFps !== null) {
    info(`${processingFps} FPS are fresh pose results.`);
  }

  if (cameraFps !== null && cameraFps <= rules.smoothAboveFps) {
    cause(
      `The camera delivers only ${cameraFps} FPS — fresh pose results can never be more frequent than the camera's frames (low light often lowers it).`
    );
  } else if (cameraFps !== null) {
    info(`The camera delivers ${cameraFps} FPS.`);
  }

  if (maxInferenceFps !== null) {
    const text = `The pose model takes ${avgInferenceMs} ms per frame here, so the model alone can reach at most ~${maxInferenceFps} FPS.`;
    if (maxInferenceFps <= rules.smoothAboveFps) {
      cause(`${text} The processor/GPU is too slow for it.`);
    } else {
      info(text);
    }
  }

  if (measured.delegate === 'CPU') {
    cause('The pose model runs on the CPU — GPU acceleration was not available.');
  } else if (environment?.gpuSoftwareRendered) {
    cause(
      `The GPU is software-emulated (${environment.gpuRenderer}) — there is no real hardware acceleration.`
    );
  }

  if (busySkipPercent !== null && busySkipPercent >= 50) {
    cause(
      `The device was still busy with the previous frame on ${busySkipPercent}% of the loop's ticks — it cannot keep up.`
    );
  }
  if (maxThrottleLevel !== null && maxThrottleLevel > 0) {
    cause(
      `Detections were slow, so the app throttled itself (level ${maxThrottleLevel}) and ran the model less often.`
    );
  }

  const cores = environment?.hardwareConcurrency ?? null;
  if (cores !== null && cores < 4) {
    cause(`Only ${cores} CPU cores.`);
  }
  const ram = environment?.deviceMemoryGb ?? null;
  if (ram !== null && ram < rules.minRamGb) {
    cause(`Only ${ram} GB RAM reported by the browser.`);
  }

  if (!lines.some(line => line.kind === 'cause')) {
    info('No bottleneck found — the device keeps up with the pose loop.');
  }
  return lines;
}
